import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "@/data/store";
import { AssessmentType, ExamTiming, TaskKind } from "@/generated/graphql";

export const selectAssignment = (state: RootState) => state.assignment;

//////////////////////////////////////////////////////////////////////////////
// Common                                                                   //
//////////////////////////////////////////////////////////////////////////////

/** Select released Assessment format. */
export const selectAssessmentFormat = createSelector(
  selectAssignment,
  (assn) => assn.format
);

/** Select the released task kind. */
export const selectTaskKind = createSelector(
  selectAssignment,
  (assn) => assn.taskKind
);

export const selectIsViva = createSelector(
  selectAssignment,
  (assn) => assn.taskKind === TaskKind.Viva
);

/** Select if the assessment has group assessment enabled. */
export const selectGroupAssessmentEnabled = createSelector(
  selectAssignment,
  (assn) => assn.groupAssessmentEnabled
);

/** Select if the assessment has a viva (oral response) enabled. */
export const selectVivaEnabled = createSelector(
  selectAssignment,
  (assn) => assn.vivaEnabled
);

/**
 * Term for the student's working period in UI copy. Viva assessments respond
 * rather than write, so "writing time" reads wrong there.
 */
export const responseTimeTerm = (vivaEnabled: boolean) =>
  vivaEnabled ? "response time" : "writing time";

/** Titled duration label for the requirements/exam-details row. */
export const responseTimeTitle = (vivaEnabled: boolean) =>
  vivaEnabled ? "Total response time" : "Writing time";

/**
 * Select whether draft feedback should be always available to students,
 * regardless of the draft due date. Today the UI ships a single toggle for
 * both individual and group flows, so this returns `true` if either flag is
 * set.
 *
 * TODO: split into `selectIndividualDraftFeedbackAlwaysAvailable` and
 * `selectGroupDraftFeedbackAlwaysAvailable` once product surfaces separate
 * controls for individual vs group drafts.
 */
export const selectDraftFeedbackAlwaysAvailable = createSelector(
  selectAssignment,
  (assn) =>
    assn.individualDraftFeedbackAlwaysAvailable ||
    assn.groupDraftFeedbackAlwaysAvailable
);

/** Select whether group drafts are enabled. */
export const selectGroupDraftEnabled = createSelector(
  selectAssignment,
  (assn) => assn.groupDraftEnabled
);

/** Select whether the teacher has disabled Cadmus spell check
 *  for this assessment.
 */
export const selectIsSpellCheckDisabled = createSelector(
  selectAssignment,
  (assn) => assn.spellCheckDisabled
);

/**
 * Select the effective spell check source applied to the editors. The teacher's
 * `spellCheckDisabled` policy is authoritative: when set, the source is always
 * `null` regardless of the student's chosen source.
 */
export const selectSpellCheckSource = createSelector(
  selectAssignment,
  (assn) => (assn.spellCheckDisabled ? null : assn.spellCheckSource)
);

/** Select if Generative AI is allowed in the assessment */
export const selectAllowGenerativeAi = createSelector(
  selectAssignment,
  (assn) => assn.allowGenerativeAi
);

/** Select sheet's referencing style. */
export const selectReferencingStyle = createSelector(
  selectAssignment,
  (assn) => assn.referencingStyle
);

/** Predicate for using hanging indent style references. */
export const selectHasHangingIndent = createSelector(
  selectAssignment,
  (assn) => assn.hangingIndent
);

/** Select whether footnotes wordcount should be part of the total word limit */
export const selectCountFootnotes = createSelector(
  selectAssignment,
  (assn) => assn.countFootnotes
);

/** Select whether references wordcount should be part of the total word limit */
export const selectCountReferences = createSelector(
  selectAssignment,
  (assn) => assn.countReferences
);

/** Select the submit attempt limit for the assignment. */
export const selectSubmitAttemptLimit = createSelector(
  selectAssignment,
  (assn) => assn.submitAttemptLimit
);

//////////////////////////////////////////////////////////////////////////////
// Exams                                                                    //
//////////////////////////////////////////////////////////////////////////////

/** Select if the current assessment is a exam. */
export const selectIsExam = createSelector(
  selectAssignment,
  (assn) => assn.type === AssessmentType.Exam
);

export type AssessmentNoun = "assignment" | "exam" | "assessment";

/**
 * Select the noun used to refer to this assessment in UI copy.
 *
 * Individual tasks (standard assignments and exams) use their specific names —
 * "assignment" or "exam" — because these are established, meaningful terms for
 * students in those contexts.
 *
 * Any other task kind resolves to the neutral "assessment". This is intentional
 * as the product evolves towards learning journeys: new task kinds (e.g. Oral
 * Assessment, formerly "Viva") reuse the exam data model for simplicity but
 * must never expose "exam" terminology to students, since that word carries the
 * wrong meaning for those products. Falling back to "assessment" ensures UI
 * copy stays accurate without requiring per-kind handling at every call site.
 */
export const selectAssessmentNoun = createSelector(
  selectTaskKind,
  selectIsExam,
  (taskKind, isExam): AssessmentNoun => {
    if (taskKind !== TaskKind.Individual) return "assessment";
    return isExam ? "exam" : "assignment";
  }
);

/** Select configured exam type. */
export const selectExamTiming = createSelector(
  selectAssignment,
  (assn) => assn.examTiming
);

/** Select whether backtrack navigation is disabled in exam */
export const selectExamBacktrackDisabled = createSelector(
  selectAssignment,
  (assn) =>
    assn.type === AssessmentType.Exam
      ? assn.examBacktrackDisabled === true
      : false
);

/** Select if current assginment is a Windowed Exam */
export const selectIsWindowExam = createSelector(
  selectIsExam,
  selectExamTiming,
  (isExam, timing) => isExam && timing === ExamTiming.Window
);

/** Select if current assginment is a Live Exam */
export const selectIsLiveExam = createSelector(
  selectIsExam,
  selectExamTiming,
  (isExam, timing) => isExam && timing === ExamTiming.Live
);

/** Select total number of minutes that the exam can go on for. */
export const selectExamTotalTime = createSelector(
  selectAssignment,
  (assn) => (assn.writingTime ?? 0) + (assn.readingTime ?? 0)
);

/** Select if the assignment has auto-submissions enabled. */
export const selectHasAutoSubmission = createSelector(
  selectAssignment,
  (assn) => assn.autoSubmission
);

/** Select if the exam is closed for further submissions. */
export const selectExamIsOver = createSelector(
  selectAssignment,
  (assn) => assn.examIsOver
);

/** Select if the exam is marked as deferred for the student. */
export const selectExamIsDeferred = createSelector(
  selectAssignment,
  selectIsExam,
  (assn, isExam) => isExam && assn.examDeferred
);

/** Select if exam have ldb enabled */
export const selectIsLockDownExam = createSelector(
  selectAssignment,
  selectIsExam,
  (assn, isExam) => isExam && assn.isLockdownBrowser
);

//////////////////////////////////////////////////////////////////////////////
// Assignments                                                              //
//////////////////////////////////////////////////////////////////////////////

/** Select if the current assessment is an assignment. */
export const selectIsAssignment = createSelector(
  selectAssignment,
  (assn) => assn.type === AssessmentType.Assignment
);

/** Select if the current assignment is time limited. */
export const selectIsTimedAssignment = createSelector(
  selectAssignment,
  (assn) => assn.type === AssessmentType.Assignment && assn.timeLimit !== null
);

/** Select is late resubmissions are allowed in the current assignment. */
export const selectAllowLateResubmission = (state: RootState) =>
  !selectIsExam(state) && selectAssignment(state).allowLateResubmission;

/** Select if the assignment time limit is an extension. */
export const selectTimeLimitExtended = createSelector(
  selectAssignment,
  (assn) => assn.timeLimitExtended
);

export const selectLateSubmissionTimeLimit = createSelector(
  selectAssignment,
  (assn) => assn.lateSubmissionTimeLimit
);

export const selectAnonymousMarking = createSelector(
  selectAssignment,
  (assn) => assn.anonymousMarking
);
